import { useEffect, useState } from 'react';
import { storeService } from '../services/storeService';
import { StoreSeller, StoreProfile, PaymentMethod, CreateOrderPaymentMethodRequest } from '../models';
import { useCartStore } from '../store/cartStore';
import { useAuthStore } from '../store/authStore';
import { useToastStore } from '../store/toastStore';
import { EmptyState } from '../components/ui/EmptyState';
import { Button } from '../components/ui/Button';
import { Link, useNavigate } from 'react-router-dom';
import { buildWhatsAppUrl } from '../utils/whatsapp';
import { OrderSuccessScreen } from './checkout/OrderSuccessScreen';
import { StepIndicator } from './checkout/StepIndicator';
import { GuestInfoStep } from './checkout/GuestInfoStep';
import { SellerStep } from './checkout/SellerStep';
import { PaymentStep } from './checkout/PaymentStep';
import { ConfirmStep } from './checkout/ConfirmStep';
import { ALL_PAYMENT_METHODS, GuestInfo, PaymentEntry, PaymentMode, Step } from './checkout/types';

const CHECKOUT_STORAGE_KEY = 'babychic-checkout-state';

const DEFAULT_GUEST_INFO: GuestInfo = {
  name: '',
  phone: '',
  neighborhood: '',
  city: '',
  email: '',
  cpfCnpj: '',
  babyName: '',
  babyBirthdate: '',
};

interface PersistedCheckoutState {
  step?: Step;
  guestInfo?: GuestInfo;
  paymentEntries?: PaymentEntry[];
  paymentMode?: PaymentMode;
  observation?: string;
  isGift?: boolean;
  selectedSellerId?: number;
}

function loadPersistedCheckoutState(): PersistedCheckoutState {
  try {
    const raw = sessionStorage.getItem(CHECKOUT_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    return parsed as PersistedCheckoutState;
  } catch {
    return {};
  }
}

export function CheckoutPage() {
  const { items, clearCart, totalPrice } = useCartStore();
  const { isAuthenticated, customerId } = useAuthStore();
  const navigate = useNavigate();
  const showToast = useToastStore((s) => s.show);

  const [persisted] = useState(loadPersistedCheckoutState);

  const [sellers, setSellers] = useState<StoreSeller[]>([]);
  const [loadingSellers, setLoadingSellers] = useState(true);
  const [step, setStep] = useState<Step>(() => persisted.step ?? (isAuthenticated ? 'seller' : 'guest-info'));
  const [selectedSeller, setSelectedSeller] = useState<StoreSeller | null>(null);
  const [customerProfile, setCustomerProfile] = useState<StoreProfile | null>(null);
  const [guestInfo, setGuestInfo] = useState<GuestInfo>(() => persisted.guestInfo ?? DEFAULT_GUEST_INFO);
  const [paymentEntries, setPaymentEntries] = useState<PaymentEntry[]>(() => {
    if (persisted.paymentEntries && persisted.paymentEntries.length > 0) {
      const restoredTotal = persisted.paymentEntries.reduce((sum, e) => sum + e.amountPaid, 0);
      if (Math.abs(restoredTotal - totalPrice()) < 0.01) {
        return persisted.paymentEntries;
      }
    }
    return [{ paymentMethod: PaymentMethod.Pix, amountPaid: totalPrice() }];
  });
  const [paymentMode, setPaymentMode] = useState<PaymentMode>(persisted.paymentMode ?? 'combine');
  const [observation, setObservation] = useState(persisted.observation ?? '');
  const [isGift, setIsGift] = useState(persisted.isGift ?? false);
  const [submitting, setSubmitting] = useState(false);
  const [completedOrder, setCompletedOrder] = useState<{ orderId: number; whatsAppUrl: string } | null>(null);

  useEffect(() => {
    storeService
      .getSellers()
      .then((data) => {
        setSellers(data);
        if (persisted.selectedSellerId) {
          const found = data.find((s) => s.userId === persisted.selectedSellerId);
          if (found) setSelectedSeller(found);
        }
      })
      .finally(() => setLoadingSellers(false));
    if (customerId) {
      storeService.getProfile().then(setCustomerProfile).catch(() => {});
    }
  }, [customerId, persisted]);

  useEffect(() => {
    try {
      const toSave: PersistedCheckoutState = {
        step,
        guestInfo,
        paymentEntries,
        paymentMode,
        observation,
        isGift,
        selectedSellerId: selectedSeller?.userId,
      };
      sessionStorage.setItem(CHECKOUT_STORAGE_KEY, JSON.stringify(toSave));
    } catch {
    }
  }, [step, guestInfo, paymentEntries, paymentMode, observation, isGift, selectedSeller]);

  if (completedOrder) {
    return (
      <OrderSuccessScreen
        orderId={completedOrder.orderId}
        whatsAppUrl={completedOrder.whatsAppUrl}
        onBackToStore={() => navigate('/')}
      />
    );
  }

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <EmptyState
          title="Carrinho vazio"
          action={<Link to="/"><Button>Ver produtos</Button></Link>}
        />
      </div>
    );
  }

  const total = totalPrice();

  const totalPaid = paymentEntries.reduce((sum, e) => sum + e.amountPaid, 0);
  const paymentRemainder = total - totalPaid;

  const addPaymentMethod = () => {
    const used = new Set(paymentEntries.map((e) => e.paymentMethod));
    const next = ALL_PAYMENT_METHODS.find((pm) => !used.has(pm));
    if (!next) return;
    setPaymentEntries((prev) => [...prev, { paymentMethod: next, amountPaid: 0 }]);
  };

  const removePaymentMethod = (index: number) => {
    setPaymentEntries((prev) => prev.filter((_, i) => i !== index));
  };

  const updatePaymentEntry = (index: number, field: keyof PaymentEntry, value: number | PaymentMethod) => {
    setPaymentEntries((prev) =>
      prev.map((e, i) => {
        if (i !== index) return e;
        const updated = { ...e, [field]: value };
        if (field === 'paymentMethod') {
          updated.installments = (value as PaymentMethod) === PaymentMethod.CreditCard ? 1 : undefined;
        }
        return updated;
      })
    );
  };

  const fillRemainder = (index: number) => {
    const othersPaid = paymentEntries
      .filter((_, i) => i !== index)
      .reduce((sum, e) => sum + e.amountPaid, 0);
    const remainder = Math.max(0, total - othersPaid);
    updatePaymentEntry(index, 'amountPaid', remainder);
  };

  const isPaymentValid = () => {
    if (paymentEntries.some((e) => e.amountPaid <= 0)) return false;
    return Math.abs(paymentRemainder) < 0.01;
  };

  const selectCombinePaymentMethod = (method: PaymentMethod) => {
    setPaymentEntries([{ paymentMethod: method, amountPaid: total }]);
  };

  const switchToDetailedPayment = () => {
    setPaymentMode('detailed');
  };

  const switchToCombinePayment = () => {
    setPaymentMode('combine');
    setPaymentEntries([{ paymentMethod: PaymentMethod.Pix, amountPaid: total }]);
  };

  const handleSubmit = async () => {
    if (!selectedSeller) return;
    setSubmitting(true);

    try {
      const paymentMethods: CreateOrderPaymentMethodRequest[] = paymentEntries.map((e) => ({
        paymentMethod: e.paymentMethod,
        amountPaid: e.amountPaid,
      }));

      const orderItems = items.map((i) => {
        const unitPrice = i.promotionalPrice ?? i.price;
        return {
          productId: i.productId,
          productVariantId: i.variantId,
          quantity: i.quantity,
          unitPrice,
          totalPrice: unitPrice * i.quantity,
        };
      });

      const isGuest = !isAuthenticated;

      let finalObservation = observation || '';
      if (isGift) {
        finalObservation = finalObservation ? `[PRESENTE] ${finalObservation}` : '[PRESENTE]';
      }
      if (isGuest) {
        const missing: string[] = [];
        if (!guestInfo.email) missing.push('e-mail');
        if (!guestInfo.cpfCnpj) missing.push('CPF/CNPJ');
        if (!guestInfo.babyName) missing.push('nome(s) do(s) filho(s)');
        if (!guestInfo.babyBirthdate) missing.push('data de nascimento do filho');
        if (missing.length > 0) {
          const note = `[Dados pendentes - solicitar ao cliente: ${missing.join(', ')}]`;
          finalObservation = finalObservation ? `${finalObservation}\n${note}` : note;
        }
      }

      const orderId = await storeService.createOrder({
        customerId: isGuest ? undefined : customerId!,
        sellerId: selectedSeller.userId,
        status: 6,
        channel: 5,
        saleDate: new Date().toISOString(),
        observation: finalObservation || undefined,
        items: orderItems,
        paymentMethods,
        ...(isGuest && {
          guestName: guestInfo.name,
          guestPhone: guestInfo.phone,
          guestNeighborhood: guestInfo.neighborhood,
          guestCity: guestInfo.city,
          guestEmail: guestInfo.email || undefined,
          guestCpfCnpj: guestInfo.cpfCnpj || undefined,
          guestBabyName: guestInfo.babyName || undefined,
          guestBabyBirthdate: guestInfo.babyBirthdate || undefined,
        }),
      });

      const displayName = isGuest ? guestInfo.name : customerProfile?.name;
      const displayPhone = isGuest ? guestInfo.phone : customerProfile?.phone;
      const whatsAppUrl = buildWhatsAppUrl(selectedSeller, items, orderId, paymentEntries, displayName, displayPhone, isGift, observation || undefined);
      setCompletedOrder({ orderId, whatsAppUrl });
      clearCart();
      try {
        sessionStorage.removeItem(CHECKOUT_STORAGE_KEY);
      } catch {
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.errors?.[0] ||
        err?.response?.data?.message ||
        'Erro ao registrar pedido. Tente novamente.';
      showToast(msg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-4 sm:py-8">
      <button
        onClick={() => navigate('/cart')}
        className="inline-flex items-center gap-2 text-sm font-medium text-text-medium hover:text-chocolate border border-champagne-dark hover:border-chocolate bg-white hover:bg-champagne px-4 py-2 rounded-full mb-3 sm:mb-6 transition-all duration-150"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Voltar para o carrinho
      </button>

      <div className="flex items-center gap-3 mb-3 sm:mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-text-dark">Finalizar pedido</h1>
      </div>

      <StepIndicator step={step} isAuthenticated={isAuthenticated} />

      {step === 'guest-info' && (
        <GuestInfoStep
          guestInfo={guestInfo}
          onChange={setGuestInfo}
          onNext={() => setStep('seller')}
        />
      )}

      {step === 'seller' && (
        <SellerStep
          sellers={sellers}
          loading={loadingSellers}
          selectedSeller={selectedSeller}
          onSelect={(seller) => {
            setSelectedSeller(seller);
            setStep('payment');
          }}
        />
      )}

      {step === 'payment' && (
        <PaymentStep
          total={total}
          paymentEntries={paymentEntries}
          paymentRemainder={paymentRemainder}
          paymentMode={paymentMode}
          onSelectCombineMethod={selectCombinePaymentMethod}
          onSwitchToDetailed={switchToDetailedPayment}
          onSwitchToCombine={switchToCombinePayment}
          onAddPaymentMethod={addPaymentMethod}
          onRemove={removePaymentMethod}
          onUpdate={updatePaymentEntry}
          onFillRemainder={fillRemainder}
          onBack={() => setStep('seller')}
          onNext={() => setStep('confirm')}
          isValid={isPaymentValid()}
        />
      )}

      {step === 'confirm' && (
        <ConfirmStep
          seller={selectedSeller!}
          paymentEntries={paymentEntries}
          total={total}
          observation={observation}
          onObservationChange={setObservation}
          isGift={isGift}
          onIsGiftChange={setIsGift}
          items={items}
          onBack={() => setStep('payment')}
          onSubmit={handleSubmit}
          submitting={submitting}
          guestInfo={!isAuthenticated ? guestInfo : undefined}
        />
      )}
    </div>
  );
}
